// @ts-strict-ignore
import {Component, computed, inject, input, Input, output} from "@angular/core";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {Translations} from "@translations";
import {AssessmentItem, ChecklistItemType} from "@models";
import {SimAssessmentScoreService, SimConfigurationService, SimMaterialDialogService} from "@services";
import {AssessmentContextService, RemoveItemParams} from "../services/assessment-context.service";
import {AssessmentAnswerService} from "../services/assessment-answer.service";
import {FormControl, FormGroup} from "@angular/forms";
import {AssessablePanelMode} from "@common/components/assessment/utils";
import {AssessableUiService} from "../services/assessable-ui.service";
import {AssessableItemData} from "../assessable-item-template.directive";

@Component({
	selector: "sim-assessment-item",
	templateUrl: "./assessment-item.component.html",
	styleUrls: ["./assessment-item.component.scss"],
	animations: [zoomInOnEnterAnimation({duration: 250}), zoomOutOnLeaveAnimation({duration: 250})],
	standalone: false,
})
export class AssessmentItemComponent {
	protected readonly AssessablePanelMode = AssessablePanelMode;
	protected readonly T = Translations;
	protected readonly ChecklistItemType = ChecklistItemType;
	protected readonly SimConfigurationService = SimConfigurationService;

	@Input({required: true}) data: AssessableItemData<AssessmentItem>;
	@Input() hideScore: boolean;
	@Input() hideExpandButton: boolean;
	@Input() hideExpansionArea: boolean;
	@Input() isInArchive: boolean;
	@Input() compactLayout = false;
	public readonly isHintsDialogEnabled = input<boolean>(false);

	onTimestampClicked = output<void>();

	protected store = inject(AssessableUiService);
	protected contextService = inject(AssessmentContextService);
	protected answerService = inject(AssessmentAnswerService);
	private _simAssessmentScoreService = inject(SimAssessmentScoreService);
	readonly #dialogService = inject(SimMaterialDialogService);

	// signals
	public isItemInEditMode = computed(() => this.store.itemInEditMode() === this.data.item.assessableItemId);
	public isItemExpanded = computed(() => this.store.itemExpanded() === this.data.item.assessableItemId);

	// in edit mode, the answer control is not available in the form state service, so we create a temporary control
	// this control is irrelevant in other modes, because the answer control is always available then
	private _tempAnswerControl = new FormGroup({
		answer: new FormControl(),
		freeTextScore: new FormControl(0),
	});

	public get itemControl() {
		return this.contextService.findItemControl(this.data.item.assessableItemId);
	}

	public get answerItemControl() {
		return this.answerService.findItemControl(this.data.item.assessableItemId) ?? this._tempAnswerControl;
	}

	public get isReadOnly() {
		return this.data.item.fromCatalogueId !== null;
	}

	public getItemScore(item: AssessmentItem, formValue: typeof this.answerItemControl.value) {
		const updatedItem: AssessmentItem = {...item, ...formValue};
		let itemScore = this._simAssessmentScoreService.getItemScore(updatedItem);

		const isAnswered = Array.isArray(updatedItem.answer)
			? updatedItem.answer.length > 0
			: updatedItem.answer !== null && updatedItem.answer !== undefined;
		if (isNaN(itemScore) || !isAnswered) {
			return "-";
		}

		return itemScore;
	}

	public getItemMaxScore(item: AssessmentItem) {
		let itemMaxScore = this._simAssessmentScoreService.getItemMaxScore(item);
		if (isNaN(itemMaxScore)) {
			return "-";
		}
		return itemMaxScore;
	}

	public mapToChoiceItem(item: AssessmentItem) {
		return {
			answer: this.answerItemControl?.controls.answer.value,
			options: item.selectableOptions.map((o) => o.option),
		};
	}

	public handleAnswerChanged(value: unknown) {
		const answerControl = this.answerItemControl.controls.answer;
		if (answerControl.value === value) {
			answerControl.setValue(null);
		} else {
			answerControl.setValue(value);
		}
	}

	public handleMultipleChoiceAnswerChanged({checked, index}: {index: number; checked: boolean}) {
		let selectedOptions = Array.isArray(this.answerItemControl.value.answer) ? [...this.answerItemControl.value.answer] : [];

		if (checked && !selectedOptions.includes(index)) {
			selectedOptions.push(index);
		} else if (!checked && selectedOptions.includes(index)) {
			selectedOptions = selectedOptions.filter((value) => value !== index);
		}

		this.answerItemControl.controls.answer.setValue(selectedOptions);
	}

	public createRemoveItemEvent(index: number, groupIndex: number | null): RemoveItemParams {
		return {itemIndex: index, groupIndex: groupIndex};
	}

	protected openHintsProcedureAndReasoningDialog() {
		this.#dialogService
			.openAssessmentItemProcedureAndReasoningDialog({
				item: this.data.item,
			})
			.then();
	}
}
